import { Injectable, Inject, PLATFORM_ID, TransferState } from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {MatDatepicker} from '@angular/material/datepicker';
import moment from 'moment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {catchError, map, retry, shareReplay, take, tap} from 'rxjs/operators';
import {EventCacheService} from '../services-cache/event-cache.service';
import {environment} from '../../environments/environment';
import {GlobalsService} from '../globals';
import {NavbarService} from './navbar.service';
import {
  CarouselPayload,
  FilterByCountryPayload,
  FilterByDatePayload,
  FilterByLocationOption,
  FilterByLocationPayload,
  ProducerPayload,
  HomepageEvent,
  HomepageCategory,
  ProducersResponse,
  CategoryTabEventPayload,
  EventsResponse,
  MainSliderSlide,
  MainSliderResponse,
} from '../interfaces/homepage.interfaces';
import { HomepageCacheService } from '../services-cache/homepage-cache.service';
import { isPlatformBrowser } from '@angular/common';
import { HOMEPAGE_CATEGORIES_KEY } from './server-homepage.service';

@Injectable({
  providedIn: 'root'
})
export class FilterEventsService {

  private _eventsByLocation$: BehaviorSubject<any[] | null> = new BehaviorSubject<any[] | null>(null);

  private _locationFilterSource$: BehaviorSubject<string> = new BehaviorSubject('');
  locationFilterSource$: Observable<string> = this._locationFilterSource$.asObservable();

  private _locationFilterValue$: BehaviorSubject<string> = new BehaviorSubject('');
  locationFilterValue$: Observable<string> = this._locationFilterValue$.asObservable();

  private _filterByLocationPayload$: BehaviorSubject<FilterByLocationPayload | null> = new BehaviorSubject<FilterByLocationPayload | null>(null);
  filterByLocationPayload$: Observable<FilterByLocationPayload | null> = this._filterByLocationPayload$.asObservable();

  private _filterByCountryPayload$: BehaviorSubject<FilterByCountryPayload | null> = new BehaviorSubject<FilterByCountryPayload | null>(null);
  filterByCountryPayload$: Observable<FilterByCountryPayload | null> = this._filterByCountryPayload$.asObservable();

  private _filterByDatePayload$: BehaviorSubject<FilterByDatePayload | null> = new BehaviorSubject<FilterByDatePayload | null>(null);
  filterByDatePayload$: Observable<FilterByDatePayload | null> = this._filterByDatePayload$.asObservable();

  private _dateFilterValue$: BehaviorSubject<string> = new BehaviorSubject('');
  dateFilterValue$: Observable<string> = this._dateFilterValue$.asObservable();

  private _filtersApplied$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  filtersApplied$: Observable<boolean> = this._filtersApplied$.asObservable();

  private _categories$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  categories$: Observable<any[]> = this._categories$.asObservable();

  datepicker = new BehaviorSubject<Datepicker>({start: '', end: ''});
  select = new BehaviorSubject<string>('');


  pickerRef: MatDatepicker<Date>; // to open it when calendar selected from dropdown
  eventsSource = new BehaviorSubject([]);
  getEventsSource = this.eventsSource.asObservable();
  categoriesSource = new BehaviorSubject([]);
  getCategoriesSource = this.categoriesSource.asObservable();
  categoriesIdsWithEvents = new BehaviorSubject([]);
  getCategoriesIdsWithEvents = this.categoriesIdsWithEvents.asObservable();
  api_base_url = environment.api_base_url;
  constructor(private http: HttpClient,
              private eventCacheService: EventCacheService,
              private globals: GlobalsService,
              private navbar: NavbarService,
              private homepageCache: HomepageCacheService,
              private transferState: TransferState,
              @Inject(PLATFORM_ID) private platformId: Object
            ) { }

  setDatepicker(val) { // app-datepicker-filter-events
    this.datepicker.next(val);
  }

  setSelect(val) { // app-select-filter-events
    this.select.next(val);
  }

  initPicker(picker) {
    this.pickerRef = picker;
  }

  openPicker() {
    this.pickerRef.open();
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  getNotUTCRange(query: string | { start: string, end: string }): { from: string, to: string } {
    const now = new Date();
    let from = "";
    let to = "";
  
    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
  
    if (query === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      from = formatLocalDate(yesterday);
      to = formatLocalDate(now);
    } else if (query === 'today') {
      from = formatLocalDate(now);
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      to = formatLocalDate(tomorrow);
    } else if (query === 'tomorrow') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      from = formatLocalDate(tomorrow);
      const dayAfterTomorrow = new Date(now);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
      to = formatLocalDate(dayAfterTomorrow);
    } else if (query === 'week') {
      from = formatLocalDate(now);
      const weekLater = new Date(now);
      weekLater.setDate(weekLater.getDate() + 7);
      to = formatLocalDate(weekLater);
    } else if (query === 'month') {
      from = formatLocalDate(now);
      const monthLater = new Date(now);
      monthLater.setDate(monthLater.getDate() + 30);
      to = formatLocalDate(monthLater);
    } else if (typeof query === 'object' && query.start && query.end) {
      from = formatLocalDate(new Date(query.start));
      to = formatLocalDate(new Date(query.end));
    }
  
    return { from, to };
  }

  getRange(query: string | { start: string, end: string }): { from: string, to: string } {
    const date = new Date();
    let from, to;

    const yesterdayDate = new Date(date);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = this.formatDate(yesterdayDate);

    const todayDate = new Date(date);
    todayDate.setHours(23, 59, 59, 0);
    const today = this.formatDate(todayDate);

    const todayEndDate = new Date(todayDate);
    todayEndDate.setDate(todayEndDate.getDate() + 1);
    const today_end = this.formatDate(todayEndDate);

    const weekDate = new Date(date);
    weekDate.setDate(weekDate.getDate() + 7);
    const week = this.formatDate(weekDate);

    const monthDate = new Date(date);
    monthDate.setDate(monthDate.getDate() + 30);
    const month = this.formatDate(monthDate);

    if (query === 'yesterday') {
      from = yesterday;
      to = today;
    }
    if (query === 'today') {
      from = today;
      to = today_end;
    }
    if (query === 'week') {
      from = today;
      to = week;
    }
    if (query === 'month') {
      from = today;
      to = month;
    }
    if (typeof query === 'object' && query.start && query.end) {
      from = this.formatDate(new Date(query.start));
      to = this.formatDate(new Date(query.end));
    }
    return { from, to };
  }

  setEventsByDate(query: string | { start: string, end: string }): void {
    if(query) {
      const { from, to } = this.getNotUTCRange(query);
      const period = typeof query === 'string' ? query : 'Calendar';
      if(from && to) { 
        this._filterByDatePayload$.next({ from, to })
        this._dateFilterValue$.next(period)
      }
    } else {
      this._filterByDatePayload$.next(null);
      this._dateFilterValue$.next('')
    }
  }

  updateFilteringStatus(): void {
    if(!this._filtersApplied$.value) {
      this._filtersApplied$.next(true);
    }
  }

 getFilteredEvents(payload: CarouselPayload | CategoryTabEventPayload): Observable<any> {
    const cacheKey = { ...payload, tab: 'events' };
    const cached = this.homepageCache.get<HomepageEvent[]>(cacheKey);
    if (cached) {
      return cached;
    }

    const path = `${this.api_base_url}/api/filter-events`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    const request$ = this.http.post<EventsResponse>(path, payload, { headers })
      .pipe(shareReplay(1));
    this.homepageCache.set(cacheKey, request$);
    return request$;
  }

  getCategoriesOfActiveEvents(): Observable<HomepageCategory[]> {
    const path = `${this.api_base_url}/api/categories`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    return this.http.get(path, { headers }).pipe(map((response: any) => response.data));
  }

  loadCategoriesOfActiveEvents(): void {
    const cacheKey = { categories: true };
    const cached = this.homepageCache.get<HomepageCategory[]>(cacheKey);
    if (cached) {
      cached.pipe(take(1)).subscribe((categories: any[]) => this._categories$.next(categories));
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      const ssrCategories = this.transferState.get(HOMEPAGE_CATEGORIES_KEY, null);
      if (ssrCategories) {
        this.transferState.remove(HOMEPAGE_CATEGORIES_KEY);
        const result$ = of(ssrCategories as HomepageCategory[]).pipe(shareReplay(1));
        this.homepageCache.set(cacheKey, result$);
        this._categories$.next(ssrCategories as HomepageCategory[]);
        return;
      }
    }

    const path = `${this.api_base_url}/api/categories`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    const request$ = this.http.get(path, { headers }).pipe(
      map((response: any) => response.data),
      shareReplay(1)
    );
    this.homepageCache.set(cacheKey, request$);
    request$.pipe(
      take(1),
      retry(3),
      catchError(err => {
        console.error('Category fetch failed', err);
        return of([]);
      }),
      tap((categories: any[]) => this._categories$.next(categories)),
    ).subscribe();
  }


  /**
   * @param isNewPlatform `true` asks for the NEW platform's category on this id
   *                      (`?is_new_platform=1`); otherwise the monolith's.
   */
  getCategoryById(id: number | string, isNewPlatform = false): Observable<any> {
    const path = `${this.api_base_url}/api/categories/${id}`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    const params = isNewPlatform ? new HttpParams().set('is_new_platform', '1') : undefined;
    return this.http.get(path, { headers, params }).pipe(map((response: any) => response.data));
  }

  getMainSliderSlides(): Observable<MainSliderSlide[]> {
    const cacheKey = { homepageMainSliderData: true };
    const cached = this.homepageCache.get<MainSliderSlide[]>(cacheKey);
    if (cached) {
      return cached;
    }
    const path = `${this.api_base_url}/api/sliders`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    const request$ = this.http.get<MainSliderResponse>(path, { headers })
      .pipe(
        map((response: MainSliderResponse) => response.sliders),
        shareReplay(1)
      );
    this.homepageCache.set(cacheKey, request$);
    return request$;
  }


  setInitialDates(payload: FilterByDatePayload | null, period: string): void {
    this._filterByDatePayload$.next(payload);
    this._dateFilterValue$.next(period);
  }

  setInitialLocation(payload: FilterByLocationPayload, city: string, source): void {
    this._filterByLocationPayload$.next(payload);
    this._locationFilterValue$.next(city);
    this._locationFilterSource$.next(source);
  }

  setInitialCountry(payload: FilterByCountryPayload) : void {
      this._filterByCountryPayload$.next(payload);
      this._locationFilterValue$.next(payload.country);
  }

  setSelectedLocation(selectedLocation: FilterByLocationOption | null): void {
    if(selectedLocation) {
      const { lon, lat, name, country_code } = selectedLocation; 
      const payload =  { location: { lat, lon, country: country_code }};
      this._filterByLocationPayload$.next(payload);
      this._locationFilterValue$.next(name);
    } else {
      this._filterByLocationPayload$.next(null);
      this._filterByCountryPayload$.next(null);
      this._locationFilterValue$.next('');
      this._locationFilterSource$.next('');
    }
  }

  getEventsByLocation(location: any): void {
    this._locationFilterValue$.next(location.title);
    if(location) {
      const type = location.type;
      const payload = type === 'city' ? { city: location.title } : { country: location.title };
      const path = `${this.api_base_url}/api/filter-events`;
      const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
      
      this.http.post(path, payload, { headers }).pipe(
        take(1),
        tap((response: any) => {
          this._eventsByLocation$.next(response.events);
        })
      ).subscribe();
    } else {
      this._eventsByLocation$.next(null);
    }
  }

  getCategoryPageStats(payload: CarouselPayload): Observable<any>{
    const path = `${this.api_base_url}/api/main-page/tab-counts`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    return this.http.post(path, payload, { headers });
  }


  getProducers(payload: ProducerPayload = {}, page: number = 1, per_page: number = 6, isCategoryPage = false): Observable<ProducersResponse>{
    const url = isCategoryPage ? '/api/top-producers-paginated' : '/api/main-page/top-producers';
    let params = new HttpParams();
    if(page) {
      params = params.set('page', page)
    }
    if(per_page) {
      params = params.set('per_page', per_page)
    }
    const path = `${this.api_base_url}${url}`;
    const headers = new HttpHeaders({'X-Requested-With': 'XMLHttpRequest'});
    const request$ = this.http.post<ProducersResponse>(path, payload, { headers, params })
      .pipe(
        shareReplay(1));
    return request$;
  }

  updateProducerFollowers(currentProducers, producerAction) {
    return currentProducers.map(producer => {
      if (producer.id === producerAction.producer_id) {
        return {
          ...producer,
          is_following: producerAction.action === 'follow',
          followers: (producerAction.action === 'follow') ? ++producer.followers : --producer.followers
        };
      }
      return producer;
    });
  }

  updateFollowers(producerAction, userId) {
    // console.log('producerAction updateFollowers', producerAction, userId);

    const eventsSource = this.eventsSource.getValue();
    let defaultData = JSON.parse(JSON.stringify(eventsSource));

    // console.log('BEFORE updateFollowers1', eventsSource);
    eventsSource.forEach((element, index) => {
      if (producerAction.producer_id === element.user_id) {

        if (producerAction.action == 'follow') {
          defaultData[index].isFollowing = true;
        }

        if (producerAction.action == 'unfollow') {
          defaultData[index].isFollowing = false;
        }

      }
    });
    // console.log('AFTER updateFollowers2', defaultData);

  }

}
interface Datepicker {
  start?: string;
  end: string;
}
